// Pagina apertura nuovo ticket dal portale clienti.
// Form semplificato con react-hook-form + zod: titolo, descrizione, sede, priorità.
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Send, AlertCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Alert } from '../../components/ui/Alert'
import { Card } from '../../components/ui/Card'
import { Skeleton } from '../../components/ui/Skeleton'
import { portalApiCalls, portalApi } from './portal.api'
import { usePortalStore } from './portal.store'

// ---------------------------------------------------------------
// Schema validazione
// ---------------------------------------------------------------

const newTicketSchema = z.object({
  siteId: z.string().uuid('Seleziona una sede valida'),
  title: z.string().min(5, 'Il titolo deve avere almeno 5 caratteri').max(200),
  description: z.string().max(3000).optional(),
  priority: z.enum(['low', 'medium', 'high', 'critical']).optional(),
})

type NewTicketFormData = z.infer<typeof newTicketSchema>

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa — intervento non urgente' },
  { value: 'medium', label: 'Media — entro 24-48 ore' },
  { value: 'high', label: 'Alta — entro poche ore' },
  { value: 'critical', label: 'Critica — intervento immediato' },
]

// ---------------------------------------------------------------
// Componente
// ---------------------------------------------------------------

export function PortalNewTicketPage() {
  const navigate = useNavigate()
  const companyId = usePortalStore((s) => s.companyId)

  // Carica le sedi dell'azienda
  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ['portal', 'sites', companyId],
    queryFn: async () => {
      const res = await portalApi.get<Array<{ id: string; name: string; address: string }>>(
        `/../../sites?companyId=${companyId}&limit=50`
      )
      return res.data
    },
    enabled: Boolean(companyId),
    // Fallback: array vuoto se le sedi non si caricano
    placeholderData: [],
  })

  const siteOptions = (sites ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }))

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<NewTicketFormData>({
    resolver: zodResolver(newTicketSchema),
    defaultValues: { priority: 'medium' },
  })

  const createMutation = useMutation({
    mutationFn: (data: NewTicketFormData) =>
      portalApiCalls.createTicket({
        siteId: data.siteId,
        title: data.title,
        description: data.description,
        priority: data.priority,
      }),
    onSuccess: ({ data }) => {
      navigate(`/portal/tickets/${data.id}`)
    },
  })

  const mutationError = createMutation.error
    ? createMutation.error instanceof Error
      ? createMutation.error.message
      : 'Errore durante la creazione del ticket.'
    : null

  const selectedSite = watch('siteId')
  const selectedPriority = watch('priority')

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
          onClick={() => navigate(-1)}
          aria-label="Torna indietro"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Indietro
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Nuova richiesta di intervento</h1>
          <p className="text-sm text-gray-500">
            Compila il modulo per aprire un nuovo ticket di assistenza
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Form principale */}
        <div className="lg:col-span-2">
          <Card>
            <form
              onSubmit={handleSubmit((d) => createMutation.mutate(d))}
              noValidate
              className="space-y-5"
            >
              {mutationError && (
                <Alert variant="error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    {mutationError}
                  </div>
                </Alert>
              )}

              {/* Sede */}
              {sitesLoading ? (
                <Skeleton height={64} className="rounded-lg" />
              ) : (
                <Select
                  label="Sede"
                  options={siteOptions}
                  value={selectedSite}
                  onValueChange={(v) => setValue('siteId', v)}
                  placeholder="Seleziona la sede dell'intervento..."
                  error={errors.siteId?.message}
                  required
                />
              )}

              {/* Titolo */}
              <Input
                label="Descrizione breve del problema"
                placeholder="Es. Condizionatore non funziona al piano 3"
                error={errors.title?.message}
                required
                {...register('title')}
              />

              {/* Descrizione dettagliata */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="description"
                  className="text-sm font-medium text-gray-900"
                >
                  Dettagli aggiuntivi
                  <span className="ml-1.5 text-xs font-normal text-gray-400">(opzionale)</span>
                </label>
                <textarea
                  id="description"
                  rows={5}
                  placeholder="Descrivi il problema in dettaglio: quando si è verificato, sintomi osservati, eventuali tentativi di risoluzione..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-xs text-red-500">{errors.description.message}</p>
                )}
              </div>

              {/* Priorità */}
              <Select
                label="Priorità"
                options={PRIORITY_OPTIONS}
                value={selectedPriority ?? 'medium'}
                onValueChange={(v) =>
                  setValue('priority', v as 'low' | 'medium' | 'high' | 'critical')
                }
                error={errors.priority?.message}
              />

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(-1)}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  isLoading={createMutation.isPending}
                  leftIcon={<Send className="h-4 w-4" />}
                >
                  Invia richiesta
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Pannello informativo */}
        <div className="space-y-4">
          <Card className="bg-blue-50 border-blue-200">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">
              Come funziona?
            </h3>
            <ol className="space-y-2 text-sm text-gray-600" role="list">
              <li className="flex gap-2">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white">
                  1
                </span>
                Compila il modulo con i dettagli del problema
              </li>
              <li className="flex gap-2">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white">
                  2
                </span>
                Il team KleisNote riceve la notifica e assegna un tecnico
              </li>
              <li className="flex gap-2">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--fc-primary)] text-xs font-bold text-white">
                  3
                </span>
                Traccia lo stato in tempo reale dal tuo portale
              </li>
            </ol>
          </Card>

          <Card>
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Urgenza critica?
            </h3>
            <p className="mb-3 text-sm text-gray-500">
              Per emergenze che richiedono intervento immediato, chiama
              direttamente il nostro supporto.
            </p>
            <a
              href="tel:+390000000000"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--fc-primary)] hover:underline"
            >
              Chiama supporto urgente
            </a>
          </Card>
        </div>
      </div>
    </div>
  )
}
